import {
  clamp,
  getPendingImagePreviewFilter,
  getPendingImageWarmthOverlay,
} from '~/components/Common/Images/cropUtils';

import type { MediaImage } from '../types';

export interface CropArea {
  height: number;
  width: number;
  x: number;
  y: number;
}

export interface LocalImageEdit {
  brightness: number;
  contrast: number;
  crop: CropArea;
  cropMode: 'fixed' | 'freeform';
  filename: string;
  title: string;
  warmth: number;
}

const warmthOverlaySx = {
  position: 'absolute',
  inset: 0,
  mixBlendMode: 'soft-light',
  pointerEvents: 'none',
};

export function buildImageFilter(edit?: LocalImageEdit) {
  return getPendingImagePreviewFilter({
    brightness: edit?.brightness ?? 0,
    contrast: edit?.contrast ?? 0,
    warmth: edit?.warmth ?? 0,
  });
}

export function getImageWarmthOverlaySx(edit?: LocalImageEdit) {
  const warmthOverlay = getPendingImageWarmthOverlay({
    brightness: edit?.brightness ?? 0,
    contrast: edit?.contrast ?? 0,
    warmth: edit?.warmth ?? 0,
  });

  return warmthOverlay.opacity
    ? {
        ...warmthOverlaySx,
        bgcolor: warmthOverlay.color,
        opacity: warmthOverlay.opacity,
      }
    : undefined;
}

export function hasImagePixelEdit(edit: LocalImageEdit) {
  return (
    !!edit.brightness ||
    !!edit.contrast ||
    !!edit.warmth ||
    edit.crop.x !== 0 ||
    edit.crop.y !== 0 ||
    edit.crop.width !== 100 ||
    edit.crop.height !== 100
  );
}

export function getImageCropPixels(edit: LocalImageEdit, dimensions: { height: number; width: number }) {
  return {
    x: (edit.crop.x / 100) * dimensions.width,
    y: (edit.crop.y / 100) * dimensions.height,
    width: (edit.crop.width / 100) * dimensions.width,
    height: (edit.crop.height / 100) * dimensions.height,
  };
}

export type CropHandle =
  | 'move'
  | 'top-left'
  | 'top'
  | 'top-right'
  | 'right'
  | 'bottom-right'
  | 'bottom'
  | 'bottom-left'
  | 'left';

export const MIN_CROP_SIZE = 18;

export function buildFilename(image: MediaImage) {
  const publicIdParts = image.publicId.split('/');
  const filename = publicIdParts[publicIdParts.length - 1];

  return filename.includes('.') ? filename : `${filename}.jpg`;
}

export function buildDefaultEdit(image: MediaImage): LocalImageEdit {
  return {
    brightness: 0,
    contrast: 0,
    crop: {
      height: 100,
      width: 100,
      x: 0,
      y: 0,
    },
    cropMode: 'fixed',
    filename: buildFilename(image),
    title: image.title || '',
    warmth: 0,
  };
}

export function mergeEditWithDefaults(defaultEdit?: LocalImageEdit, edit?: LocalImageEdit) {
  if (!defaultEdit) {
    return undefined;
  }

  if (!edit) {
    return defaultEdit;
  }

  return {
    ...defaultEdit,
    ...edit,
    crop: edit.crop ?? defaultEdit.crop,
  };
}

export function getFixedCrop(crop: CropArea, imageAspectRatio = 16 / 9, fixedAspectRatio = 16 / 9) {
  const { height, width } = getFixedCropSize(crop, imageAspectRatio, fixedAspectRatio);
  const centreX = crop.x + crop.width / 2;
  const centreY = crop.y + crop.height / 2;

  return {
    height,
    width,
    x: clamp(centreX - width / 2, 0, 100 - width),
    y: clamp(centreY - height / 2, 0, 100 - height),
  };
}

function getFixedCropSize(
  crop: Pick<CropArea, 'height' | 'width'>,
  imageAspectRatio = 16 / 9,
  fixedAspectRatio = 16 / 9,
) {
  const horizontalFixedAspectRatio = Math.max(fixedAspectRatio, 1 / fixedAspectRatio);
  const heightForWidth = (width: number) => (width * imageAspectRatio) / horizontalFixedAspectRatio;
  const widthForHeight = (height: number) => (height * horizontalFixedAspectRatio) / imageAspectRatio;
  const maxWidth = Math.min(crop.width, widthForHeight(crop.height), 100);
  const maxHeight = Math.min(crop.height, heightForWidth(maxWidth), 100);
  const height = clamp(maxHeight, MIN_CROP_SIZE, 100);
  const width = clamp(widthForHeight(height), MIN_CROP_SIZE, 100);

  return {
    height,
    width,
  };
}

export function calculateCrop(
  handle: CropHandle,
  crop: CropArea,
  deltaX: number,
  deltaY: number,
  cropMode: LocalImageEdit['cropMode'],
  imageAspectRatio = 16 / 9,
  fixedAspectRatio = 16 / 9,
) {
  if (handle === 'move') {
    return {
      ...crop,
      x: clamp(crop.x + deltaX, 0, 100 - crop.width),
      y: clamp(crop.y + deltaY, 0, 100 - crop.height),
    };
  }

  const right = crop.x + crop.width;
  const bottom = crop.y + crop.height;
  let nextLeft = crop.x;
  let nextTop = crop.y;
  let nextRight = right;
  let nextBottom = bottom;

  if (handle.includes('left')) {
    nextLeft = clamp(crop.x + deltaX, 0, right - MIN_CROP_SIZE);
  }

  if (handle.includes('right')) {
    nextRight = clamp(right + deltaX, crop.x + MIN_CROP_SIZE, 100);
  }

  if (handle.includes('top')) {
    nextTop = clamp(crop.y + deltaY, 0, bottom - MIN_CROP_SIZE);
  }

  if (handle.includes('bottom')) {
    nextBottom = clamp(bottom + deltaY, crop.y + MIN_CROP_SIZE, 100);
  }

  const nextWidth = nextRight - nextLeft;
  const nextHeight = nextBottom - nextTop;

  if (cropMode === 'fixed') {
    if (handle.includes('-')) {
      const fixedCropSize = getFixedCropSize(
        {
          height: nextHeight,
          width: nextWidth,
        },
        imageAspectRatio,
        fixedAspectRatio,
      );
      const fixedLeft = handle.includes('left') ? right - fixedCropSize.width : crop.x;
      const fixedTop = handle.includes('top') ? bottom - fixedCropSize.height : crop.y;

      return {
        ...fixedCropSize,
        x: clamp(fixedLeft, 0, 100 - fixedCropSize.width),
        y: clamp(fixedTop, 0, 100 - fixedCropSize.height),
      };
    }

    return getFixedCrop(
      {
        height: nextHeight,
        width: nextWidth,
        x: nextLeft,
        y: nextTop,
      },
      imageAspectRatio,
      fixedAspectRatio,
    );
  }

  return {
    height: nextHeight,
    width: nextWidth,
    x: nextLeft,
    y: nextTop,
  };
}
